import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AppButton from '@/components/buttons/AppButton';
import NoDataAvailable from '@/components/cards/NoDataAvailable';
import { LinesStackSkeleton } from "@/components/skeletons";
import SearchBar from '@/components/navbars/SearchBar';
import SharedAccessTable from '@/components/tables/SharedAccessTable';
import { deleteAccountDelegation, fetchSharedAccessUsersForMyAccount, fetchSharedAccessUsersForOther } from "@/services/user.service";
import { SHARED_ACCESS } from '@/utils/constants/appConstants/SharedAcessConstant';
import { NO_SHARED_ACCESS_GRANTED_MESSAGE, NO_SHARED_ACCESS_GRANTED_SUB_MESSAGE, NO_SHARED_ACCESS_MESSAGE, NO_SHARED_ACCESS_SUB_MESSAGE } from "@/utils/constants/messages";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import { getListCacheBootstrap, invalidateListCacheByPage, writeListCache } from "@/utils/listPageCache";


const SharedAccess = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as any;
    const params = useParams();

    const meBootstrap = getListCacheBootstrap("shared-access-me", { userId: params.id, search: null });
    const otherBootstrap = getListCacheBootstrap("shared-access-other", { userId: params.id, search: null });

    const [shareWithMe, setShareWithMe] = useState<any[]>(meBootstrap.items as any[]);
    const [shareWithOther, setShareWithOther] = useState<any[]>(otherBootstrap.items as any[]);
    const [shareWithMeLoader, setShareWithMeLoader] = useState(!meBootstrap.hasCache);
    const [shareWithOtherLoader, setShareWithOtherLoader] = useState(!otherBootstrap.hasCache);
    const [searchItemSharedWithMe, setSearchItemSharedWithMe] = useState<string | null>(null);
    const [searchItemSharedWithOther, setSearchItemSharedWithOther] = useState<string | null>(null);
    const [showWithOtherSearchBar, setShowWithOtherSearchBar] = useState(false);
    const [showWithMeSearchBar, setShowWithMeSearchBar] = useState(false);
    const [activeTab, setActiveTab] = useState<"others" | "me">("others");

    useEffect(() => {
        const userId = params.id as string;
        const { cacheKey, cached } = getListCacheBootstrap("shared-access-me", {
            userId,
            search: searchItemSharedWithMe,
        });

        if (cached) {
            setShareWithMe(cached.items as any[]);
            setShareWithMeLoader(false);
        } else {
            setShareWithMeLoader(true);
        }

        fetchSharedAccessUsersForMyAccount(userId, searchItemSharedWithMe as string).then((res) => {
            const nextData = res.data ?? [];
            writeListCache(cacheKey, nextData, nextData.length);
            setShareWithMeLoader(false);
            setShareWithMe(nextData);
        });
    }, [params.id, searchItemSharedWithMe]);

    useEffect(() => {
        const userId = params.id as string;
        const { cacheKey, cached } = getListCacheBootstrap("shared-access-other", {
            userId,
            search: searchItemSharedWithOther,
        });

        if (cached) {
            setShareWithOther(cached.items as any[]);
            setShareWithOtherLoader(false);
        } else {
            setShareWithOtherLoader(true);
        }

        fetchSharedAccessUsersForOther(userId, searchItemSharedWithOther as string).then((res) => {
            const nextData = res.data ?? [];
            writeListCache(cacheKey, nextData, nextData.length);
            setShareWithOtherLoader(false);
            setShareWithOther(nextData);
        });
    }, [params.id, searchItemSharedWithOther]);

    const resetSharedWithMeData = () => setSearchItemSharedWithMe("");
    const resetSharedWithOtherData = () => setSearchItemSharedWithOther("");
    const searchSharedWithMe = (searchItem: string) => setSearchItemSharedWithMe(searchItem === "" ? null : searchItem);
    const searchSharedWithOther = (searchItem: string) => setSearchItemSharedWithOther(searchItem === "" ? null : searchItem);

    const handleRemoveDelegation = (id: string) => {
        deleteAccountDelegation(id, params.id as string)
            .then((res) => {
                successToasterMessage(SHARED_ACCESS.DELETE_SUCCESSFULLY);
                invalidateListCacheByPage("shared-access-other");
                setShareWithOther(res.data);
            })
            .catch((err) => {
                errorToasterMessage(err.response.data.message);
            });
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                    <h3 className="text-xl font-normal leading-snug text-secondary" id="shared-access-title">{SHARED_ACCESS.HEADING}</h3>
                    <p id="shared-access-sub-title" className="mt-1 text-sm text-muted-foreground">{SHARED_ACCESS.SUB_HEADING}</p>
                </div>
                <div>
                    <AppButton id="shared-with-other-btn" btnText={SHARED_ACCESS.SHARE_ACCESS_WITH_OTHERS} className={"btn-primary"} onClick={() => navigate(`/shared-access/${params.id}/create`, { replace: true, state: { user: state.user } })} />
                </div>
            </div>
            <ul className="gs-page-tabs">
                <li>
                    <button
                        className={`gs-page-tab${activeTab === "others" ? " gs-page-tab--active" : ""}`}
                        id="shared-access-others-tab"
                        onClick={() => setActiveTab("others")}
                    >
                        {SHARED_ACCESS.SHARED_WITH_OTHERS}
                    </button>
                </li>
                <li>
                    <button
                        className={`gs-page-tab${activeTab === "me" ? " gs-page-tab--active" : ""}`}
                        id="shared-access-me-tab"
                        onClick={() => setActiveTab("me")}
                    >
                        {SHARED_ACCESS.SHARED_WITH_ME((location.state as any)?.user.first_name)}
                    </button>
                </li>
            </ul>
            <div>
                {activeTab === "others" && (
                    <div className="gs-list-page-card">
                        <div className="gs-list-page-card-header">
                            {showWithOtherSearchBar ? (
                                <SearchBar id={"shared-access-searchbar"} onHide={() => setShowWithOtherSearchBar(false)} handleSearch={searchSharedWithOther} reset={resetSharedWithOtherData} title={""} />
                            ) : (
                                <div className="gs-list-page-header">
                                    <div />
                                    <div className="gs-list-page-tools">
                                        <Button type="button" variant="ghost" size="icon" onClick={() => setShowWithOtherSearchBar(true)}>
                                            <em id="shared-access-search-icon" className="icon ni ni-search" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            {!shareWithOtherLoader ? (
                                shareWithOther.length > 0 ? (
                                    <SharedAccessTable data={shareWithOther} isDeleteAccountDelegationAllowed={true} handleRemoveAccess={handleRemoveDelegation} />
                                ) : (
                                    <NoDataAvailable>
                                        <h5 className="text-center">{NO_SHARED_ACCESS_MESSAGE}</h5>
                                        <br />
                                        <p className="text-center">{NO_SHARED_ACCESS_SUB_MESSAGE}</p>
                                    </NoDataAvailable>
                                )
                            ) : (
                                <LinesStackSkeleton rowCount={4} className="space-y-3 px-4 py-8" />
                            )}
                        </div>
                    </div>
                )}
                {activeTab === "me" && (
                    <div className="gs-list-page-card">
                        <div className="gs-list-page-card-header">
                            {showWithMeSearchBar ? (
                                <SearchBar id={"shared-access-searchbar"} onHide={() => setShowWithMeSearchBar(false)} handleSearch={searchSharedWithMe} reset={resetSharedWithMeData} title={""} />
                            ) : (
                                <div className="gs-list-page-header">
                                    <div />
                                    <div className="gs-list-page-tools">
                                        <Button type="button" variant="ghost" size="icon" onClick={() => setShowWithMeSearchBar(true)}>
                                            <em id="shared-access-search-icon" className="icon ni ni-search" />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            {!shareWithMeLoader ? (
                                shareWithMe.length > 0 ? (
                                    <SharedAccessTable data={shareWithMe} isDeleteAccountDelegationAllowed={false} />
                                ) : (
                                    <NoDataAvailable>
                                        <h5 className="text-center">{NO_SHARED_ACCESS_GRANTED_MESSAGE}</h5>
                                        <br />
                                        <p className="text-center">{NO_SHARED_ACCESS_GRANTED_SUB_MESSAGE}</p>
                                    </NoDataAvailable>
                                )
                            ) : (
                                <LinesStackSkeleton rowCount={4} className="space-y-3 px-4 py-8" />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedAccess;
